import {
  Button,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  UseDisclosureReturn,
} from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';

import {
  CommitmentMechanicsFormData,
  LenderTableData,
  ServicingFeesData,
} from '../types';
import { useCreditAgreementStore } from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import { SelectInput } from '~/core/routes/settings/implementations/common/Inputs';
import { CreditAgreementFullArtifact } from '~/core/routes/settings/implementations/common/types';
import { Typography } from '~/packages/common-frontend/components/Typography';

type LenderType = {
  lenderId: string;
  lenderName: string;
  lenderSlug: string;
};

export const getSelectOptions = (
  capitalProviders: CreditAgreementFullArtifact['facilityMechanics']['parties']['capitalProviders'],
  currentLenderTableEntities: LenderTableData[],
  currentServicingFeeTableEntities: ServicingFeesData[],
  isServicingFeeTable: boolean,
) => {
  const lenders = capitalProviders.map(({ entity }) => ({
    label: entity.name,
    value: entity.id,
    lenderData: {
      lenderId: entity.id,
      lenderName: entity.name,
      lenderSlug: entity.slug,
    },
  }));

  // Lenders already added to Lender Table
  const lendersSet = new Set(
    currentLenderTableEntities.map((lender) => lender.lenderId),
  );

  // Lenders already added to Servicing Fee Table
  const servicingFeesSet = new Set(
    currentServicingFeeTableEntities.map((lender) => lender.lenderId),
  );

  if (!isServicingFeeTable) {
    // Remove all lenders already added to the Lender Table
    return lenders.filter(
      (lender) => !lendersSet.has(lender.lenderData.lenderId),
    );
  }
  // Return all lenders in Lender Table, excluding any that are already in Servicing Fee table
  return currentLenderTableEntities
    .filter((lender) => !servicingFeesSet.has(lender.lenderId))
    .map(({ lenderId, lenderName, lenderSlug }) => ({
      label: lenderName,
      value: lenderId,
      lenderData: {
        lenderId,
        lenderName,
        lenderSlug,
      },
    }));
};

type AddLenderModalProps = {
  disclosure: UseDisclosureReturn;
  onAddLender: (lender: LenderType) => void;
  isServicingFeeTable?: boolean;
};
export const AddLenderModal = ({
  disclosure,
  onAddLender,
  isServicingFeeTable = false,
}: AddLenderModalProps) => {
  const parentForm = useFormContext<CommitmentMechanicsFormData>();
  const form = useForm<LenderType>({
    defaultValues: {
      lenderId: '',
    },
    mode: 'onTouched',
  });
  const { isOpen, onClose } = disclosure;

  const currentLenderTableEntities = parentForm.getValues('wipForm.lenders');
  const currentServicingFeeTableEntities = parentForm.getValues(
    'wipForm.servicingFees',
  );

  const capitalProviders = useCreditAgreementStore(
    (store) =>
      store.creditAgreement?.facilityMechanics.parties.capitalProviders ?? [],
  );

  const formOptions = getSelectOptions(
    capitalProviders,
    currentLenderTableEntities,
    currentServicingFeeTableEntities,
    isServicingFeeTable,
  );

  useEffect(() => {
    if (isOpen) form.reset();
  }, [form, isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit((data) => {
              const lender = formOptions.find(
                (lenderOption) => lenderOption.value === data.lenderId,
              );
              if (lender) {
                onAddLender(lender.lenderData);
              }
              disclosure.onClose();
            })}
          >
            <ModalHeader>Add Lender</ModalHeader>
            <ModalCloseButton />
            <ModalBody display="flex" flexDirection="column" gap="1rem">
              <SelectInput<LenderType>
                name="lenderId"
                helperText={
                  <>
                    Configure lenders in{' '}
                    <Link as={RouterLink} to="../parties">
                      <Typography
                        inline
                        variant="detail-muted"
                        colorScheme="blue"
                        fontWeight="medium"
                        trim
                      >
                        parties.
                      </Typography>
                    </Link>
                  </>
                }
                placeholder={
                  formOptions.length ? 'Select' : 'No Lenders Available'
                }
                required
                options={formOptions}
              />
            </ModalBody>
            <ModalFooter>
              <Button
                variant="secondary"
                colorScheme="v2-aurora"
                mr={3}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button colorScheme="v2-aurora" type="submit">
                Add Lender
              </Button>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
};
